import React, { useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import Paper from '@material-ui/core/Paper';
import { createStyles, Theme, makeStyles } from '@material-ui/core/styles';
import CircularProgress from '@material-ui/core/CircularProgress';
import Grid from '@material-ui/core/Grid';
import { showToast, BackTo } from '@deeptrue-frontend/ui';

import { useFormatMessage } from 'components/IntlProvider';
import { Dashboard } from 'components/Layout';
import api from 'api/api';
import StoreProvider from 'components/StoreProvider/StoreProvider';
import { ProductsReducerType } from 'store/modules/products/products.types';
import Forms from 'components/Forms';
import RouterProvider from 'components/RouterProvider/RouterProvider';

const { ROUTES } = RouterProvider;
const { connect, selectors, actions } = StoreProvider;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
      maxWidth: '420px',
      marginTop: theme.spacing(3),
      padding: theme.spacing(2),
      border: '1px solid #CECECA',
      boxShadow: ' 0 4px 12px 0 rgba(0,0,0,0.1)',
      borderRadius: '10px',
    },
  })
);

interface ProductProps extends RouteComponentProps<{ slug: string }> {
  product: ProductsReducerType['selected'];
  setProduct: (payload: ProductsReducerType['selected']) => void;
  clearProduct: () => void;
}

const Product = ({
  product,
  setProduct,
  clearProduct,
  location,
  history,
  match: { params },
}: ProductProps) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const classes = useStyles();
  const t = useFormatMessage();
  const isEditMode = params.slug !== 'create';

  const submitWorkbench = (values: ProductsReducerType['selected']) => {
    if (isEditMode) {
      return api.resources
        .updateProduct(values)
        .then(() => {
          history.push(ROUTES.ADMIN.PRODUCTS.LIST);
        })
        .catch((err) => {
          Forms.helpers.throwError(err.response.data);
        });
    }
    return api.resources
      .createProduct(values)
      .then(() => {
        history.push(ROUTES.ADMIN.PRODUCTS.LIST);
      })
      .catch((err) => {
        Forms.helpers.throwError(err.response.data);
      });
  };

  const getProduct = () => {
    setIsLoaded(false);
    api.resources
      .getProduct({ slug: params.slug })
      .then(({ data }) => {
        setProduct(data);
        setIsLoaded(true);
      })
      .catch(() => {
        showToast({
          type: 'error',
          title: t('errors.toast.product', 'The product given cannot be found'),
        });
        history.push(ROUTES.ADMIN.WORKBENCHES.LIST);
      });
  };

  useEffect(() => {
    if (isEditMode) {
      getProduct();
    } else {
      setIsLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    return () => {
      clearProduct();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Dashboard
      title={isEditMode ? product.name : 'New product'}
      location={location}
    >
      <BackTo onClick={() => history.push('/admin/products')}>
        {t('backTo.admin.products', 'Back to Products')}
      </BackTo>
      <Paper className={classes.root}>
        {isLoaded ? (
          <Forms.ProductInfo
            initialValues={{
              ...product,
              briefing_template:
                product.briefing_template?.id !== -1
                  ? product.briefing_template
                  : '',
              workbench_id: isEditMode ? product.workbench_id : '',
              workbench: product.workbench?.id !== -1 ? product.workbench : '',
              isEditMode,
            }}
            onSubmit={submitWorkbench}
          />
        ) : (
          <Grid container justify="center">
            <CircularProgress />
          </Grid>
        )}
      </Paper>
    </Dashboard>
  );
};

export default connect(
  (state) => ({
    product: selectors.products.selected(state),
  }),
  {
    setProduct: actions.products.setProduct,
    clearProduct: actions.products.clearBriefing,
  }
)(Product);
